import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Queue } from '@shared/util/hooks/useQueue';
import { isPopoverShown } from '@shared/util/popover';
import { startViewTransition } from '@shared/util/startViewTransition';
import { Toast } from './Toast';
import { QueuedToast } from './useToastCreator';
import styles from './ToastPresenter.module.css';

export function ToastPresenter({ queue }: { queue: Queue<QueuedToast> }) {
  const [items, setItems] = useState<QueuedToast[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    promoteToTopLayer(container);
    startViewTransition(() => {
      setItems(queue.items);
    }, container);
  }, [queue.items]);

  // Portal into <body>: while a non-modal Dialog is open, it makes the app root
  // inert, and inertness is inherited by DOM ancestry even in the top layer. As a
  // direct <body> child the popover container is exempted and toasts stay
  // clickable above dialogs.
  return createPortal(
    <div className={styles.container} ref={containerRef} popover="manual">
      <div className={styles.topContainer}>
        {items.filter(isPositionTop).map((t, index) => (
          <ToastRenderer key={t.id} toast={t} index={index} />
        ))}
      </div>
      <div className={styles.bottomContainer}>
        {items.filter(isPositionBottom).map((t, index) => (
          <ToastRenderer key={t.id} toast={t} index={index} />
        ))}
      </div>
    </div>,
    document.body,
  );
}

// Hide and re-show the container popover to keep toasts in the top layer.
function promoteToTopLayer(container: HTMLElement) {
  if (isPopoverShown(container)) {
    container.hidePopover();
  }
  container.showPopover();
}

function isPositionTop(toast: QueuedToast) {
  return toast.position === 'top';
}

function isPositionBottom(toast: QueuedToast) {
  return toast.position === 'bottom';
}

function ToastRenderer({ toast, index }: { toast: QueuedToast; index: number }) {
  const { remove, ...toastProps } = toast;
  return <Toast {...toastProps} onClose={remove} autoClose={toastProps.autoClose && index === 0} />;
}
